import type { VocabularyEntry } from "../../runtime/types";
import { buildVocabularyLetterSequence } from "./vocabularyOrthography";

export type VocabularyExerciseKind = "listen_repeat" | "listen_write" | "unscramble";

export const LISTEN_REPEAT_XP_PER_CLEAR = 10;

export function resolveVocabularyExerciseRawScore(kind: VocabularyExerciseKind, hintStage = 0): number {
  if (kind !== "unscramble" || hintStage <= 0) return 10;
  return hintStage === 1 ? 6 : 3;
}

const EXCLUDED_ENGLISH_EXERCISE_LEXICAL_UNITS = new Set([
  "be fine",
  "give me a moment",
  "give me an example",
  "give me an update",
  "let me finish",
  "let me fix that",
  "let me see",
  "let me think",
  "let me update that",
  "send me an example",
  "send me an update",
  "send me the link",
  "stop you there",
]);

const EXCLUDED_ITALIAN_EXERCISE_LEXICAL_UNITS = new Set([
  "ti ascolto",
]);

const APPROVED_ENGLISH_EXERCISE_LEXICAL_UNITS = new Set([
  "when it comes to",
]);

const ENGLISH_BE_NON_FORMULA_REGEX = /^be\s+(?!right\s+back\b)(able\s+to\b|more\s+specific\b|difficult\b|processed\b|sent\b|corrected\b|stamped\b|checked\b|notified\b|careful\b|clear(?:er)?\b|interested\b|honest\b|added\b|raised\b)/u;
const ENGLISH_SOCIAL_FORMULA_TO_REGEX = /^(good|great|nice|lovely|glad|happy|sorry)\s+to\s+(see|meet|hear)\b/u;
const ENGLISH_SUBORDINATE_SUBJECT_CLAUSE_REGEX = /^(before|after|if|when|while|because|although|though|since|as)\s+(i|we|you|they|he|she|it)\b/u;
const ENGLISH_WH_CONTRACTION_CLAUSE_REGEX = /^(what|where|when|who|how|why)'s\s+[\p{L}']/u;
const ENGLISH_OPEN_QUESTION_STARTER_REGEX = /^(what|where|when|why|how|who)\s+(do|does|did|can|could|would|will|should|is|are|was|were|has|had|have|led|makes|matters|caused|happened)\b/u;
const ENGLISH_FINITE_CLAUSE_PATTERN_REGEX = /^[a-z']+\s+(is|are|was|were|will|would|can|could|should|might|must|does|did|has|had)\s+[a-z']/u;
const ENGLISH_CLAUSAL_YOU_PATTERN_REGEX = /^(before|after|as|if|when|what|where|why)\s+you\b|\b(will|would|can|could|should|might|must|do|does|did|have|has|had)\s+you\b|\b(is|are|was|were|works|worked|will|would|can|could|should|might|must|do|does|did|have|has|had)\s+(for|with|to)\s+you\b/u;

export interface VocabularyExerciseEligibility {
  listenRepeatIds: string[];
  listenWriteIds: string[];
  unscrambleIds: string[];
  totalExerciseCount: number;
}

function hashVocabularyKey(value: string): number {
  let hash = 2166136261;
  for (const char of String(value || "")) {
    // eslint-disable-next-line no-bitwise -- compact 32-bit mixing keeps ordering deterministic across runtimes.
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  // eslint-disable-next-line no-bitwise -- normalize back into the unsigned 32-bit range for stable sort ranking.
  return hash >>> 0;
}

function resolveVocabularyItemId(entry: VocabularyEntry): string {
  return String(entry.vocabularyId || entry.display || entry.lemma || "").trim();
}

function resolveVocabularyTerm(entry: VocabularyEntry): string {
  return String(entry.display || entry.lemma || "").trim();
}

function normalizeExerciseLexicalUnit(entry: VocabularyEntry): string {
  return resolveVocabularyTerm(entry)
    .trim()
    .toLocaleLowerCase("en-US")
    .replace(/[’`]/g, "'")
    .replace(/\s+/g, " ");
}

function isEnglishExercisePromptLikeChunk(entry: VocabularyEntry): boolean {
  const normalizedTerm = normalizeExerciseLexicalUnit(entry);
  if (APPROVED_ENGLISH_EXERCISE_LEXICAL_UNITS.has(normalizedTerm)) {
    return false;
  }
  if (EXCLUDED_ENGLISH_EXERCISE_LEXICAL_UNITS.has(normalizedTerm)) {
    return true;
  }
  if (ENGLISH_BE_NON_FORMULA_REGEX.test(normalizedTerm)) {
    return true;
  }
  if (ENGLISH_SOCIAL_FORMULA_TO_REGEX.test(normalizedTerm)) {
    return true;
  }
  if (ENGLISH_SUBORDINATE_SUBJECT_CLAUSE_REGEX.test(normalizedTerm)) {
    return true;
  }
  if (ENGLISH_FINITE_CLAUSE_PATTERN_REGEX.test(normalizedTerm)) {
    return true;
  }
  if (ENGLISH_WH_CONTRACTION_CLAUSE_REGEX.test(normalizedTerm)) {
    return true;
  }
  if (ENGLISH_OPEN_QUESTION_STARTER_REGEX.test(normalizedTerm)) {
    return true;
  }
  if (ENGLISH_CLAUSAL_YOU_PATTERN_REGEX.test(normalizedTerm)) {
    return true;
  }
  return false;
}

function isItalianExercisePromptLikeChunk(entry: VocabularyEntry): boolean {
  const normalizedTerm = normalizeExerciseLexicalUnit(entry);
  if (EXCLUDED_ITALIAN_EXERCISE_LEXICAL_UNITS.has(normalizedTerm)) {
    return true;
  }
  return false;
}

function isGloballyExerciseEligible(entry: VocabularyEntry): boolean {
  const id = resolveVocabularyItemId(entry);
  const term = resolveVocabularyTerm(entry);
  const audioRef = String(entry.audioRef || "").trim();
  const partOfSpeech = String(entry.partOfSpeech || "").trim().toLowerCase();
  const language = String(entry.language || "").trim().toLowerCase();

  if (!id || !term || !audioRef) {
    return false;
  }
  if (language === "en" && isEnglishExercisePromptLikeChunk(entry)) {
    return false;
  }
  if (language === "it" && isItalianExercisePromptLikeChunk(entry)) {
    return false;
  }
  if (/\d/.test(term)) {
    return false;
  }
  if (partOfSpeech.includes("proper")) {
    return false;
  }
  return /[\p{L}]/u.test(term);
}

function isUnscrambleEligible(entry: VocabularyEntry): boolean {
  const term = resolveVocabularyTerm(entry);
  if (term.split(/\s+/u).length !== 1) {
    return false;
  }
  const letterSequence = buildVocabularyLetterSequence(term, entry.language);
  if (letterSequence.length < 3) {
    return false;
  }
  return letterSequence.every((char) => /[\p{L}\p{N}']/u.test(char));
}

export function buildVocabularyExerciseEligibility(entries: VocabularyEntry[]): VocabularyExerciseEligibility {
  const listenRepeatIds: string[] = [];
  const listenWriteIds: string[] = [];
  const unscrambleIds: string[] = [];

  for (const entry of entries) {
    if (!isGloballyExerciseEligible(entry)) {
      continue;
    }
    const itemId = resolveVocabularyItemId(entry);
    listenRepeatIds.push(itemId);
    listenWriteIds.push(itemId);
    if (isUnscrambleEligible(entry)) {
      unscrambleIds.push(itemId);
    }
  }

  return {
    listenRepeatIds,
    listenWriteIds,
    unscrambleIds,
    totalExerciseCount: listenRepeatIds.length + listenWriteIds.length + unscrambleIds.length,
  };
}

export function buildVocabularyExerciseCompletionKey(kind: VocabularyExerciseKind, vocabularyId: string): string {
  return `${kind}::${String(vocabularyId || "").trim()}`;
}

export function buildVocabularyRunSnapshot(
  orderedItemIds: readonly string[],
  kind: VocabularyExerciseKind,
  completedKeys: ReadonlySet<string> = new Set(),
  replay = false,
): { itemIds: string[]; completedBeforeRun: number; totalCount: number; initialIndex: number; replay: boolean } {
  const allIds = Array.from(new Set(orderedItemIds.map((id) => id.trim()).filter(Boolean)));
  const remaining = allIds.filter((id) => !completedKeys.has(buildVocabularyExerciseCompletionKey(kind, id)));
  const replaying = replay || (kind === "listen_repeat" && allIds.length > 0 && remaining.length === 0);
  const itemIds = replaying || kind === "listen_repeat" ? allIds : remaining;
  return {
    itemIds,
    completedBeforeRun: replaying ? 0 : allIds.length - remaining.length,
    totalCount: allIds.length,
    initialIndex: kind === "listen_repeat" && !replaying && remaining.length > 0 ? allIds.indexOf(remaining[0]) : 0,
    replay: replaying,
  };
}

export function resolveRecordedVocabularyExerciseScore(
  recordedScoreByCompletionKey: Record<string, number>,
  completedCompletionKeys: ReadonlySet<string> | undefined,
  exerciseKind: VocabularyExerciseKind,
  vocabularyId: string,
): number {
  const completionKey = buildVocabularyExerciseCompletionKey(exerciseKind, vocabularyId);
  const recordedScore = Math.max(0, Number(recordedScoreByCompletionKey[completionKey] || 0));
  if (recordedScore > 0) {
    return recordedScore;
  }
  if (exerciseKind === "listen_repeat" && completedCompletionKeys?.has(completionKey)) {
    return LISTEN_REPEAT_XP_PER_CLEAR;
  }
  return 0;
}

export function buildStableVocabularyExerciseOrder(itemIds: string[], orderKey = ""): string[] {
  return [...itemIds]
    .map((itemId, index) => ({
      itemId,
      index,
      rank: hashVocabularyKey(orderKey ? `${orderKey}::${itemId}::${index}` : `${itemId}::${index}`),
    }))
    .sort((left, right) => {
      if (left.rank !== right.rank) {
        return left.rank - right.rank;
      }
      return left.index - right.index;
    })
    .map(({ itemId }) => itemId);
}

export function buildModeSpecificVocabularyExerciseOrder(
  itemIds: string[],
  exerciseKind: VocabularyExerciseKind,
  contextKey = "",
): string[] {
  const kinds: VocabularyExerciseKind[] = ["listen_repeat", "unscramble", "listen_write"];
  const priorOrders: string[][] = [];
  for (const kind of kinds) {
    const candidate = buildStableVocabularyExerciseOrder(itemIds, `${contextKey}::${kind}`);
    let distinctOrder = candidate;
    if (candidate.length > 1) {
      for (let offset = 0; offset < candidate.length; offset += 1) {
        const rotated = [...candidate.slice(offset), ...candidate.slice(0, offset)];
        if (!priorOrders.some((prior) => prior.every((itemId, index) => itemId === rotated[index]))) {
          distinctOrder = rotated;
          break;
        }
      }
    }
    priorOrders.push(distinctOrder);
    if (kind === exerciseKind) {
      return distinctOrder;
    }
  }
  return [...itemIds];
}
